import React from 'react'
import { Box, Typography, useTheme, Stack, Tooltip } from '@mui/material'
import { ApplicationStatus } from '@otw/models'
import EditNoteIcon from '@mui/icons-material/EditNote'
import AssignmentIcon from '@mui/icons-material/Assignment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { getStatusColor, getStatusLabel, getStatusDescription } from '../constants/applicationStatus'

type UserRole = 'admin' | 'client' | 'bank'

/** Filters used by application management (includes completed, which is not a status). */
export type ApplicationStatusFilter =
  | Exclude<ApplicationStatus, 'draft'>
  | 'completed'

interface ApplicationStatusTimelineProps {
  currentStatus?: ApplicationStatus
  role?: UserRole
  /** When set with onSelect, nodes become clickable filters. */
  selectedFilter?: ApplicationStatusFilter
  onSelect?: (filter: ApplicationStatusFilter) => void
  /** Append a Completed node for admin list filtering. */
  includeCompleted?: boolean
}

const COMPLETED_COLORS = {
  main: '#26A69A',
  light: '#B2DFDB',
}

const getStatusDescriptionByRole = (status: ApplicationStatus, role: UserRole) => {
  switch (status) {
    case 'signing':
      return role === 'client'
        ? 'You are currently signing the application'
        : role === 'bank'
          ? 'Client is signing the application'
          : 'Client is signing the application'
    case 'ready':
      return role === 'client'
        ? 'Your application is ready for review'
        : role === 'bank'
          ? 'Application is ready to be assigned to an agent'
          : 'Application is ready to be assigned to a bank agent'
    case 'corrections_needed':
      return role === 'client'
        ? 'Your application needs some corrections'
        : role === 'bank'
          ? 'Application has been sent back for corrections'
          : 'Application has been sent back for corrections'
    case 'pending':
      return role === 'client'
        ? 'Your application is being reviewed by a bank agent'
        : role === 'bank'
          ? 'Application is assigned and pending your review'
          : 'Application is being reviewed by a bank agent'
    case 'viewed':
      return role === 'client'
        ? 'A bank agent has viewed your application'
        : role === 'bank'
          ? 'You have viewed the application'
          : 'Bank agent has viewed the application'
    case 'approved':
      return role === 'client'
        ? 'Your application has been approved!'
        : role === 'bank'
          ? 'You have approved this application'
          : 'Application has been approved'
    case 'denied':
      return role === 'client'
        ? 'Your application has been denied'
        : role === 'bank'
          ? 'You have denied this application'
          : 'Application has been denied'
    default:
      return getStatusDescription(status)
  }
}

const getStatusLabelByRole = (status: ApplicationStatus, role: UserRole) => {
  switch (status) {
    case 'signing':
      return role === 'client' ? 'Signing' : 'Client Signing'
    case 'ready':
      return role === 'client' ? 'Ready for Review' : 'Ready for Assignment'
    case 'corrections_needed':
      return role === 'client' ? 'Needs Corrections' : 'Corrections Requested'
    case 'pending':
      return role === 'client' ? 'Under Review' : 'Pending Review'
    case 'viewed':
      return role === 'client' ? 'Viewed by Bank' : 'Viewed'
    case 'approved':
      return role === 'client' ? 'Approved!' : 'Approved'
    case 'denied':
      return role === 'client' ? 'Denied' : 'Denied'
    default:
      return getStatusLabel(status)
  }
}

const getStatusInfo = (status: ApplicationStatus, role: UserRole = 'admin') => {
  switch (status) {
    case 'signing':
      return {
        icon: <DriveFileRenameOutlineIcon />,
        title: getStatusLabelByRole(status, role),
      }
    case 'ready':
      return {
        icon: <EditNoteIcon />,
        title: getStatusLabelByRole(status, role),
      }
    case 'corrections_needed':
      return {
        icon: <ErrorOutlineIcon />,
        title: getStatusLabelByRole(status, role),
      }
    case 'pending':
      return {
        icon: <AssignmentIcon />,
        title: getStatusLabelByRole(status, role),
      }
    case 'viewed':
      return {
        icon: <VisibilityIcon />,
        title: getStatusLabelByRole(status, role),
      }
    case 'approved':
      return {
        icon: <ThumbUpIcon />,
        title: getStatusLabelByRole(status, role),
      }
    case 'denied':
      return {
        icon: <ThumbDownIcon />,
        title: getStatusLabelByRole(status, role),
      }
    default:
      return {
        icon: <EditNoteIcon />,
        title: getStatusLabelByRole(status, role),
      }
  }
}

const statusOrder: Exclude<ApplicationStatus, 'draft' | 'denied'>[] = [
  'signing',
  'ready',
  'corrections_needed',
  'pending',
  'viewed',
  'approved',
]

type TimelineNodeProps = {
  filterKey: ApplicationStatusFilter
  title: string
  icon: React.ReactNode
  colors: { main: string; light: string }
  description: string
  selected: boolean
  interactive: boolean
  onSelect?: (filter: ApplicationStatusFilter) => void
}

const TimelineNode: React.FC<TimelineNodeProps> = ({
  filterKey,
  title,
  icon,
  colors,
  description,
  selected,
  interactive,
  onSelect,
}) => {
  const dimmed = interactive && !selected

  return (
    <Box
      component={interactive ? 'button' : 'div'}
      type={interactive ? 'button' : undefined}
      onClick={interactive ? () => onSelect?.(filterKey) : undefined}
      aria-pressed={interactive ? selected : undefined}
      aria-label={interactive ? `Filter by ${title}` : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        flex: '1 1 0',
        minWidth: { xs: 72, sm: 0 },
        zIndex: 1,
        border: 'none',
        background: 'none',
        padding: 0,
        margin: 0,
        cursor: interactive ? 'pointer' : 'default',
        opacity: dimmed ? 0.45 : 1,
        transform: selected && interactive ? 'translateY(-2px)' : 'none',
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        '&:hover': interactive
          ? {
              opacity: 1,
            }
          : undefined,
        '&:focus-visible': interactive
          ? {
              outline: `2px solid ${colors.main}`,
              outlineOffset: 4,
              borderRadius: 1,
            }
          : undefined,
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            width: interactive ? 44 : 40,
            height: interactive ? 44 : 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: colors.main,
            color: 'white',
            transition: 'all 0.3s ease',
            mb: 1,
            boxShadow: selected && interactive
              ? `0 0 0 4px ${colors.light}, 0 0 0 7px ${colors.main}`
              : `0 0 0 4px ${colors.light}`,
          }}
        >
          {icon}
        </Box>
        <Tooltip title={description} arrow placement='top'>
          <Box
            component='span'
            onClick={e => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              bgcolor: 'background.paper',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'help',
              color: colors.main,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>
      </Box>
      <Typography
        variant='caption'
        align='center'
        sx={{
          fontWeight: selected && interactive ? 700 : 600,
          color: colors.main,
          transition: 'all 0.3s ease',
          lineHeight: 1.2,
          px: 0.25,
        }}
      >
        {title}
      </Typography>
    </Box>
  )
}

export const ApplicationStatusTimeline: React.FC<ApplicationStatusTimelineProps> = ({
  role = 'admin',
  selectedFilter,
  onSelect,
  includeCompleted = false,
}) => {
  const theme = useTheme()
  const interactive = typeof onSelect === 'function'
  const nodes: ApplicationStatusFilter[] = [
    ...statusOrder,
    'denied',
    ...(includeCompleted ? (['completed'] as ApplicationStatusFilter[]) : []),
  ]

  return (
    <Box
      sx={{
        mb: interactive ? 1 : 4,
        py: interactive ? 1 : 2,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        mx: { xs: -1, sm: 0 },
        px: { xs: 1, sm: 0 },
      }}
    >
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        justifyContent='space-between'
        sx={{
          position: 'relative',
          minWidth: { xs: includeCompleted ? 640 : 560, sm: 'auto' },
          '&:before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: '25px',
            height: '2px',
            background: theme.palette.divider,
            zIndex: 0,
          },
        }}
      >
        {nodes.map((filterKey, index) => {
          const isCompleted = filterKey === 'completed'
          const status = isCompleted ? null : (filterKey as ApplicationStatus)
          const { icon, title } = isCompleted
            ? { icon: <CheckCircleOutlineIcon />, title: 'Completed' }
            : getStatusInfo(status!, role)
          const colors = isCompleted ? COMPLETED_COLORS : getStatusColor(status!)
          const description = isCompleted
            ? 'Applications marked complete'
            : getStatusDescriptionByRole(status!, role)
          const selected = interactive && selectedFilter === filterKey

          return (
            <React.Fragment key={filterKey}>
              {index > 0 && (
                <ArrowForwardIcon
                  sx={{
                    color: theme.palette.grey[400],
                    transition: 'all 0.3s ease',
                    fontSize: 20,
                    zIndex: 1,
                    flexShrink: 0,
                  }}
                />
              )}
              <TimelineNode
                filterKey={filterKey}
                title={title}
                icon={icon}
                colors={colors}
                description={description}
                selected={!!selected}
                interactive={interactive}
                onSelect={onSelect}
              />
            </React.Fragment>
          )
        })}
      </Stack>
    </Box>
  )
}
